import {
  populateBusinessFromFiling,
  populateBusinessSimpleDataFromFiling,
  populateBusinessMultiAddressFromFiling,
  populateBusinessSingleAddressFromFiling,
} from "../../../../populate/forms/businessTaxFiling";
import {
  populateFromFiling,
  populateSimpleDataFromFiling,
  populateMultiAddressFromFiling,
  populateSingleAddressFromFiling,
} from "../../../../populate/forms/personalTaxFiling";
import { formatPhoneNumber } from "../../../../formatting/phoneFormatter";
import { fromStateNameToCode } from "../../../../helpers/stateHelpers";

export function generateFormStateFromSchema({
  schemaSections,
  service,
  filing,
  schemaKey,
}) {
  /**
   * Helper to generate tax situation formState based on: schemaSections, service & filing
   */

  const state = {};
  const getValue = (fieldName, type, field = null) => {
    if (service === "PTF") {
      if (["composed_boolean_choice", "single_boolean_choice"].includes(type)) {
        return populateFromFiling(filing, fieldName);
      } else if (type === "multi_boolean_choice") {
        // Handle multi_boolean_choice fields - ensure proper structure
        const rawData = populateSimpleDataFromFiling(
          filing,
          fieldName,
          null,
          schemaKey
        );
        
        // Normalize the structure: ensure value is boolean, fields only when value is true
        if (rawData && typeof rawData === "object") {
          const value = rawData.value != null ? !!rawData.value : false;
          
          if (value === false) {
            // If value is false, don't include fields
            return { value: false };
          } else {
            // If value is true, include fields if they exist
            const result = {
              value: true,
              ...(rawData.fields && typeof rawData.fields === "object" && Object.keys(rawData.fields).length > 0
                ? { fields: rawData.fields }
                : { fields: {} })
            };
            
            return result;
          }
        }
        
        // Default structure if no data
        return { value: false };
      } else if (type === "multi_address") {
        return populateMultiAddressFromFiling(filing, fieldName, schemaKey);
      } else if (type === "single_address") {
        return populateSingleAddressFromFiling(filing, fieldName, schemaKey);
      } else if (fieldName === "incomeStates" && type === "multi_select") {
        // Special handling for incomeStates - convert to object structure if needed
        const rawData = populateSimpleDataFromFiling(
          filing,
          fieldName,
          {},
          schemaKey
        );
        
        // If it's already an object, return it
        if (rawData && typeof rawData === "object" && !Array.isArray(rawData)) {
          return rawData;
        }
        
        // If it's an array (old format), convert to object structure
        // We'll need taxYears to properly map it, but for now return empty object
        // The sync logic in TaxSituationForm will handle initialization
        return {};
      } else {
        // Use field's defaultValue if specified, otherwise use generic default
        const defaultVal = field && field.defaultValue !== undefined 
          ? field.defaultValue 
          : getDefaultValue(type);
        return populateSimpleDataFromFiling(
          filing,
          fieldName,
          defaultVal,
          schemaKey
        );
      }
    } else if (service === "BTF") {
      if (["composed_boolean_choice", "single_boolean_choice"].includes(type)) {
        const populatedData = populateBusinessFromFiling(filing, fieldName, null, schemaKey);
        // For optional single_boolean_choice fields, if value is false, return null instead of { value: false }
        // This ensures optional fields that are unchecked are properly excluded from validation
        if (type === "single_boolean_choice") {
          // Find the field definition to check if it's required
          const fieldDef = schemaSections
            .flatMap((section) => section.fields || [])
            .find((field) => field.name === fieldName);
          
          // Extract value from populatedData (it has { value } structure)
          // If populatedData is null/undefined, treat as no value
          const fieldValue = populatedData && typeof populatedData === "object" && populatedData !== null
            ? populatedData.value 
            : null;
          
          // Always return { value: boolean } structure for consistency
          // This ensures react-hook-form can properly track the field state
          return {
            value: fieldValue === true ? true : false
          };
        }
        return populatedData;
      } else if (type === "multi_boolean_choice") {
        // Handle multi_boolean_choice fields - ensure proper structure
        const rawData = populateBusinessSimpleDataFromFiling(
          filing,
          fieldName,
          null,
          schemaKey
        );
        
        // Normalize the structure: ensure value is boolean, fields only when value is true
        if (rawData && typeof rawData === "object") {
          const value = rawData.value != null ? !!rawData.value : false;
          
          if (value === false) {
            // If value is false, don't include fields
            return { value: false };
          } else {
            // If value is true, include fields if they exist
            const result = {
              value: true,
              ...(rawData.fields && typeof rawData.fields === "object" && Object.keys(rawData.fields).length > 0
                ? { fields: rawData.fields }
                : { fields: {} })
            };
            
            // Normalize nested state fields for specific fields
            if (fieldName === "stateAnnualReport" && result.fields.stateAnnualReports) {
              result.fields.stateAnnualReports = fromStateNameToCode(result.fields.stateAnnualReports);
            }
            
            return result;
          }
        }
        
        // Default structure if no data
        return { value: false };
      } else if (type === "multi_address") {
        return populateBusinessMultiAddressFromFiling(
          filing,
          fieldName,
          schemaKey
        );
      } else if (type === "single_address") {
        return populateBusinessSingleAddressFromFiling(
          filing,
          fieldName,
          schemaKey
        );
      } else {
        // Use field's defaultValue if specified, otherwise use generic default
        const defaultVal = field && field.defaultValue !== undefined 
          ? field.defaultValue 
          : getDefaultValue(type);
        return populateBusinessSimpleDataFromFiling(
          filing,
          fieldName,
          defaultVal,
          schemaKey
        );
      }
    }
  };

  const getDefaultValue = (type, fieldName) => {
    // Special case: incomeStates should default to object, not array
    if (fieldName === "incomeStates" && type === "multi_select") {
      return {};
    }
    
    switch (type) {
      case "multi_select":
      case "multi_address":
        return [];
      case "multi_property":
        return []; // Array of property objects
      case "input":
      case "textarea":
        return "";
      case "single_boolean_choice":
      case "single_select":
      case "single_address":
        return null;
      case "composed_boolean_choice":
        return 0; // used as `value` in fallback
      default:
        return null;
    }
  };

  const sectionsArray = Array.isArray(schemaSections)
    ? schemaSections
    : Object.values(schemaSections || {}).flat();

  sectionsArray.forEach((section) => {
    if (!section || !Array.isArray(section.fields)) return;
    section.fields.forEach((field) => {
      const value = getValue(field.name, field.type, field);

      if (
        field.type === "single_address" &&
        value &&
        typeof value === "object"
      ) {
        // Spread individual fields into state for validation
        Object.assign(state, value);
        if (state.suiteAddress === undefined) state.suiteAddress = "";
        if (state.country === undefined) state.country = "US";
        // Also set the consolidated object so SingleAddress component can access it
        state[field.name] = value;
      } else if (field.name === "incomeStates" && field.type === "multi_select") {
        // Ensure incomeStates is an object, not an array
        state[field.name] = value && typeof value === "object" && !Array.isArray(value) 
          ? value 
          : getDefaultValue(field.type, field.name);
      } else {
        // Phone number should be stored as digits-only for validation
        // PhoneInput component will format it for display
        if (field.name === "phone" && field.type === "input" && field.input_type === "tel" && value) {
          // Extract digits only - remove any formatting
          const digitsOnly = String(value).replace(/\D/g, "");
          state[field.name] = digitsOnly;
      } else {
        state[field.name] = value;
        }
      }
    });
  });

  state.serviceType = service;
  
  // Always include taxYears in formState, regardless of current schemaKey
  // This ensures taxYears is available for address validation even when in Background section
  if (filing?.tax_situation?.details) {
    if (service === "PTF" && filing.tax_situation.details.PTF?.situation?.taxYears) {
      const taxYears = filing.tax_situation.details.PTF.situation.taxYears;
      state.taxYears = Array.isArray(taxYears) ? taxYears : (taxYears ? [taxYears] : []);
    } else if (service === "BTF" && filing.tax_situation.details.BTF?.situation?.taxYears) {
      const taxYears = filing.tax_situation.details.BTF.situation.taxYears;
      state.taxYears = Array.isArray(taxYears) ? taxYears : (taxYears ? [taxYears] : []);
    }
  }
  
  return state;
}
